import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { EPOCH_ISO } from '../../utils/date';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { transactionsTypes } from '../transactions';

// eslint-disable-next-line camelcase
const FORMAT_LLLL_yyyy = 'LLLL yyyy';

export const makeGoalTransactions = (goal, fundingAccountId, amount, subtype) => {
  const clientId1 = uuidv4();
  const clientId2 = uuidv4();
  const fromTxn = makeGoalTransferTxn(goal, fundingAccountId, goal.accountId, -1 * amount, clientId1, clientId2);
  const toTxn = makeGoalTransferTxn(goal, goal.accountId, fundingAccountId, amount, clientId2, clientId1, subtype); // subtype only applied on goal-account side
  return [fromTxn, toTxn];
};

// currently only used in mobile because lacks QData/preprocess and meta context
export const createTransfer = (goal, fundingAccountId, amount, subtype) => {
  const txns = makeGoalTransactions(goal, fundingAccountId, amount, subtype);
  const context = amount < 0 ? 'WithdrawFromGoal' : 'ContributeToGoal';
  return { txnPack: [txns[0]?.toJS(), txns[1]?.toJS()], context };
};

export const makeGoalTransferTxn = (goal, fromAccountId, toAccountId, amount, fromClientId, toClientId, subtype) =>
  transactionsTypes.mkCashFlowTransaction({
    accountId: fromAccountId,
    payee: `${goal.name} Goal`,
    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: toAccountId,
      type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT,
    }),
    clientId: toClientId,
    type: 'CASH_FLOW',
    subtype,
    tags: goal.tagId ? [{ id: goal.tagId }] : undefined, // todo: maybe use retriever to put full tag here?
    isExcludedFromF2S: true,
    isExcludedFromReports: true,
    matchState: 'USER_UNMATCHED',
    state: 'CLEARED',
    transfer: { clientId: fromClientId },
    amount,
  });

export const findGoalAccount = (goal, goalAccounts) => {
  if (goal && goalAccounts && goalAccounts.length > 0) {
    return goalAccounts ? goalAccounts.find((goalAccount) => goalAccount.id === goal.accountId) : null;
  }
  return null;
};

export const getGoalAccountBalance = (goal, goalAccount) => goal?.savedSoFar || goalAccount?.currentBalanceAsOf || 0;

export const getAverageContribution = (goal, goalTxnsStartingMonth) => {
  const startingContribution =
    goalTxnsStartingMonth?.size > 0
      ? goalTxnsStartingMonth
          .map((txn) => (txn.subtype === 'GOAL_STARTING_AMOUNT' ? Number(txn.amount) : 0))
          .reduce((sum, amt) => sum + amt)
      : 0;
  const goalStartDate = DateTime.fromISO(goal.startOn);
  const monthsDiff = Math.ceil(DateTime.local().diff(goalStartDate, 'months').toObject().months);
  const safeMonthsDiff = monthsDiff > 1 ? monthsDiff : 1;
  return goal.savedSoFar ? (goal.savedSoFar - startingContribution) / safeMonthsDiff : 0;
};

export const getMonthlyContribution = (goalTxnsForCurrentMonth) =>
  goalTxnsForCurrentMonth?.size > 0
    ? goalTxnsForCurrentMonth
        .map((txn) => (txn.subtype !== 'GOAL_SPENT' && txn.subtype !== 'GOAL_STARTING_AMOUNT' ? Number(txn.amount) : 0))
        .reduce((sum, amt) => sum + amt)
    : 0;

export const getExpectedEndDate = (goal, goalTxnsForCurrentMonth, contribution = 0) => {
  if (isGoalCompleted(goal)) {
    return DateTime.fromISO(goal.completedOn);
  }
  const currentContribution = goal.savedSoFar || 0;
  const targetMonthlyContribution = contribution || goal.contributionAmount || 1;
  const thisMonthsContribution = getMonthlyContribution(goalTxnsForCurrentMonth);
  const adjustmentForEndOfMonth = Math.max(targetMonthlyContribution - thisMonthsContribution, 0);
  const balanceAtEndOfThisMonth = currentContribution + adjustmentForEndOfMonth;

  const monthsLeft =
    Math.ceil(Math.max(Number(goal.targetAmount) - balanceAtEndOfThisMonth, 0) / targetMonthlyContribution) || 1;

  const today = DateTime.local().startOf('month');
  const goalStartOn = DateTime.fromISO(goal.startOn);
  let startDate = today > goalStartOn ? today : goalStartOn;
  // Include current month when there is no contribution. Assume user has until end of month if there is contribution. Set startDate to last day of the month.
  startDate = balanceAtEndOfThisMonth > 0 ? startDate.endOf('month') : startDate.minus({ days: 1 }); // -1 is to include start month also
  const endDate = startDate.plus({ months: monthsLeft });
  return endDate;
};

export const formatGoalDates = (goal) => ({
  targetOn:
    goal.targetOn && goal.targetOn !== EPOCH_ISO ? DateTime.fromISO(goal.targetOn).toFormat(FORMAT_LLLL_yyyy) : null,
  startOn: DateTime.fromISO(goal.startOn).toFormat(FORMAT_LLLL_yyyy),
  completedOn: goal.completedOn ? DateTime.fromISO(goal.completedOn).toFormat(FORMAT_LLLL_yyyy) : null,
});

export const isGoalCompleted = (goal) => {
  const { completedOn } = formatGoalDates(goal);
  if (completedOn && completedOn.length > 0 && completedOn !== EPOCH_ISO) {
    if (goal.savedSoFar < goal.targetAmount) {
      return false;
    }
    return true;
  }
  if (goal.savedSoFar >= goal.targetAmount) {
    return true;
  }
  return false;
};

export const getTargetContribAmt = (goal) => (goal.contributionAmount ? goal.contributionAmount : 0);

// formats month and year string to full DateTime object
export const getFullDate = (monthString, yearString) => DateTime.fromFormat(`${monthString} ${yearString}`, 'MMM yyyy');
// Format amount string to float
export const parseAmount = (amount) => (Number.isNaN(Number.parseFloat(amount)) ? 0 : Number.parseFloat(amount));
